import React, { useMemo } from 'react'
import { Grid } from '@material-ui/core'
import { Tooltip } from '@progress/kendo-react-tooltip'
import {
  DialogFormHLPure,
  ButtonAddHLPure,
  SearchBarHLPure,
  GridSelectionHLPure,
  CheckBoxHLPure,
  TextAreaHLPure,
  InstructionsPure
} from '../../pures'

/**
 * @component DialogShareUsersPure
 * @description
 * Modal reutilizable para compartir documentos o expedientes con colaboradores o invitados.
 * Incluye pestañas, búsqueda de usuarios, checkboxes de permisos y campo de mensaje.
 *
 * @param {Object} props
 * @property {Function} t - Función de traducción i18n.
 * @property {Object} documentData - Documento actual (para mostrar su nombre).
 * @property {Array} usersGrid - Lista de usuarios disponibles (colaboradores/invitados).
 * @property {Array} permisos - Permisos configurables (opcional).
 * @property {string} userSearch - Valor del input de búsqueda.
 * @property {string} messageShare - Mensaje opcional a enviar.
 * @property {Object} handlers - Conjunto de handlers provenientes del action principal.
 * @property {string|Array<string>} [description] - Descripción o instrucciones opcionales que se muestran al inicio del modal (puede ser string o array para InstructionsPure).
 * @property {string} [submitButtonLabel] - Label personalizado para el botón de submit (opcional, por defecto usa 'documentPanelActions.share').
 * @property {string} [titleDialog] - Título personalizado para el diálogo (opcional, por defecto usa 'Compartir' + nombre documento).
 * @property {boolean} [showPermissions=true] - Mostrar o no la sección de permisos.
 * @property {boolean} [showMessage=true] - Mostrar o no la sección de mensaje.
 * @property {boolean} [showInvitedDownloadPermission=true] - Mostrar o no el checkbox de descarga para invitados.
 * @property {boolean} [showDescriptionWithSelection=true] - Si es false, oculta la descripción cuando hay usuarios seleccionados.
 * @property {boolean} [isSingleUserMode=false] - Si es true, solo permite seleccionar un usuario (modo documento).
 */
const DialogShareUsersPure = ({
  t,
  documentData,
  usersGrid = [],
  permisos = [],
  userSearch = '',
  messageShare = '',
  handlers = {},
  userSendGridSelected,
  description = '',
  submitButtonLabel = '',
  titleDialog = '',
  showPermissions = true,
  showMessage = true,
  showInvitedDownloadPermission = true,
  showDescriptionWithSelection = true,
  isSingleUserMode = false
}) => {

  const heightGrid = 340

  // Contar usuarios seleccionados
  const selectedUsersCount = useMemo(() => {
    return usersGrid.filter(u => u.checked).length
  }, [usersGrid])

  const shouldShowDescription = useMemo(() => {
    if (!description) return false
    if (showDescriptionWithSelection) return true
    return selectedUsersCount === 0
  }, [description, showDescriptionWithSelection, selectedUsersCount])

  if (!handlers || typeof handlers !== 'object') return null

  // Determinar si debe tener scroll o no
  const hasPermissionsOrMessage = showPermissions || showMessage
  const containerClass = hasPermissionsOrMessage
    ? 'documentPanelActions-dialog-share-container'
    : 'documentPanelActions-dialog-share-container documentPanelActions-dialog-share-no-scroll'

  return (
    <DialogFormHLPure
      titleDialog={titleDialog || `${t('documentPanelActions.dialog.share')} ${documentData?.name || ''}`}
      saveBtn={submitButtonLabel || t('documentPanelActions.share')}
      textBtnCancel={t('button.cancel')}
      cancel={handlers.handleOpenCloseDialogShare}
      handleSubmit={handlers.handleSubmitDialogShare}
    >
      <div className={containerClass}>
        {shouldShowDescription && (
          <div style={{ marginBottom: 16 }}>
            <InstructionsPure instructions={description} />
          </div>
        )}

        {/* Tabs: Colaboradores / Invitados */}
        <div
          className='documentPanelActions-tabs'
          style={{ display: 'flex', gap: 12, marginBottom: 12 }}
          role='tablist'
        >
          <Tooltip openDelay={600} position='top' anchorElement='target' tooltipClassName='tooltipGeneralStyle'>
            <button
              role='tab'
              type='button'
              onClick={() => handlers.handleChangeTab(process.env.REACT_APP_USER_TAB_COLLABORATORS)}
              className={`tab-btn ${handlers.activeShareTab === (process.env.REACT_APP_USER_TAB_COLLABORATORS) || handlers.activeShareTab === undefined
                ? 'active'
                : ''
                }`}
            >
              {t('digitalVault.share.collaborators') || 'Colaboradores'}
            </button>
          </Tooltip>

          <Tooltip openDelay={600} position='top' anchorElement='target' tooltipClassName='tooltipGeneralStyle'>
            <button
              role='tab'
              type='button'
              onClick={() => handlers.handleChangeTab(process.env.REACT_APP_USER_TAB_INVITED)}
              className={`tab-btn ${handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED ? 'active' : ''}`}
            >
              {t('digitalVault.share.invited') || 'Invitados'}
            </button>
          </Tooltip>
        </div>

        {/* Botones de agregar y búsqueda */}
        <div className='dialogSigner-formButtons'>
          {(handlers.activeShareTab === process.env.REACT_APP_USER_TAB_COLLABORATORS || handlers.activeShareTab === undefined) && (
            <ButtonAddHLPure
              nameBtn={t('digitalVault.share.addCollaborator')}
              onClickBtn={handlers.handleOpenAddCollaborator}
            />
          )}

          {handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED && (
            <ButtonAddHLPure
              nameBtn={t('digitalVault.share.addInvited')}
              onClickBtn={handlers.handleOpenAddInvited}
            />
          )}

          <SearchBarHLPure
            placeholder={t('digitalVault.editDocument.placeholder.search')}
            onSearchChange={handlers.filterChangeUsers}
            searchValue={userSearch}
            icon={false}
          />
        </div>

        {/* Tabla de usuarios */}
        <div className='dialog-invitation-grid'>
          <Grid item className='documentPanelActions-grid-documents'>
            <GridSelectionHLPure
              styleGeneral
              styleGeneralClass={`dialog-signer-gridSelectionGLPure ${'dialog-signer-height' + heightGrid}`}
              pageable
              skip={0}
              take={4}
              dataGrid={usersGrid}
              filter
              onSelectedItem={userSendGridSelected}
              columnTwo={false}
              columnThree={false}
              columnFive={false}
              cellColumnOne={(props) => {
                return (
                  <td className='documentPanelActions-cell-one'>
                    <div className='documentPanelActions-div-cell-one'>
                      <CheckBoxHLPure
                        valueCheckBox={props.dataItem}
                        checkedState={props.dataItem.checked}
                        onChangeHandler={(event) => { handlers.onChangeCheckShare(event, props.dataItem, isSingleUserMode) }}
                      />
                      <div className='documentPanelActions-div-user'>
                        <span className='gridHLPure-userName'>{props.dataItem.name}</span>
                        <span className='gridHLPure-fieldSubtitle'>{props.dataItem.email}</span>
                      </div>
                    </div>
                  </td>
                )
              }}
            />
          </Grid>
        </div>

        {/* Permisos y mensaje */}
        {(showPermissions || showMessage) && (
          <div className='documentPanelActions-div-actions-btns'>
            {showPermissions && (!handlers.activeShareTab || handlers.activeShareTab === process.env.REACT_APP_USER_TAB_COLLABORATORS) && (
              <h1>{t('documentPanelActions.permissions.share')}</h1>
            )}

            {showPermissions && (
              <div>
                {handlers.activeShareTab === process.env.REACT_APP_USER_TAB_INVITED ? (
                  <>
                    {showInvitedDownloadPermission && (
                      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                        {(() => {
                          const downloadPerm = (permisos || []).find((p) => p?.nameBD === 'download')
                          return (
                            <CheckBoxHLPure
                              idCheckBox='perm-invited-download'
                              labelCheckBox={t('digitalvault.share.allowGuestDownload')}
                              checkedState={!!downloadPerm?.checked}
                              onChangeHandler={(e) =>
                                downloadPerm &&
                                (handlers.onTogglePermission
                                  ? handlers.onTogglePermission(e, downloadPerm)
                                  : handlers.onClickBtnActions && handlers.onClickBtnActions(e, downloadPerm))
                              }
                            />
                          )
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                      gap: 12,
                      marginBottom: 10
                    }}
                  >
                    {permisos.map((permiso, i) => (
                      <Tooltip
                        key={`perm-tp-${i}`}
                        openDelay={600}
                        position='top'
                        anchorElement='target'
                        tooltipClassName='tooltipGeneralStyle'
                      >
                        <div title={permiso.name} style={{ display: 'flex', justifyContent: 'center' }}>
                          <CheckBoxHLPure
                            idCheckBox={`perm-${i}`}
                            labelCheckBox={permiso.name}
                            checkedState={!!permiso.checked}
                            onChangeHandler={(e) =>
                              handlers.onTogglePermission
                                ? handlers.onTogglePermission(e, permiso)
                                : handlers.onClickBtnActions && handlers.onClickBtnActions(e, permiso)
                            }
                            stackVertical
                          />
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showMessage && (
              <div className='documentPanelActions-dialog-message'>
                <h1>{t('documentPanelActions.message.share')}</h1>
                <TextAreaHLPure
                  valueTextData={messageShare}
                  handleChangeTextArea={handlers.handleChangeMessageShare}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </DialogFormHLPure>
  )
}

export default DialogShareUsersPure
